import { Autocomplete, Box, Checkbox, TextField, Typography, type AutocompleteRenderInputParams } from "@mui/material";

interface SelectEntidadeBaseProps<T extends { id?: string }> {
  label: string;
  options: T[];
  getLabel: (item: T) => string;
  // Com motivo, a opção fica desabilitada e o motivo aparece embaixo dela. Uma já selecionada
  // continua no campo e pode ser removida.
  getDisabledReason?: (item: T) => string | undefined;
  required?: boolean;
  size?: "small" | "medium";
  disabled?: boolean;
  error?: boolean;
  helperText?: string;
}

type SelectEntidadeProps<T extends { id?: string }> = SelectEntidadeBaseProps<T> &
  (
    | {
        multiple?: false;
        // Id do item selecionado; sem correspondência em options, fica vazio.
        value?: string | null;
        // Chamado com o id escolhido, ou null ao limpar.
        onChange: (id: string | null) => void;
      }
    | {
        // Várias escolhas: caixa de seleção em cada opção e as escolhidas como chips, na ordem
        // de options.
        multiple: true;
        // Ids selecionados; sem correspondência em options, ficam de fora.
        value: string[];
        onChange: (ids: string[]) => void;
      }
  );

// Autocomplete de uma lista de entidades com id, em que o valor do formulário é só o id (ou a
// lista de ids, com multiple).
export function SelectEntidade<T extends { id?: string }>(props: SelectEntidadeProps<T>) {
  const { label, options, getLabel, getDisabledReason, required, size, disabled, error, helperText } = props;
  const comuns = {
    options,
    size,
    fullWidth: true,
    disabled,
    getOptionLabel: getLabel,
    getOptionDisabled: (o: T) => !!getDisabledReason?.(o),
    isOptionEqualToValue: (a: T, b: T) => a.id === b.id,
    renderInput: (params: AutocompleteRenderInputParams) => (
      <TextField {...params} label={label} required={required} error={error} helperText={helperText} />
    ),
  };

  if (props.multiple) {
    return (
      <Autocomplete
        {...comuns}
        multiple
        disableCloseOnSelect
        value={options.filter((o) => props.value.includes(o.id!))}
        onChange={(_, novos) => props.onChange(novos.map((o) => o.id!))}
        renderOption={({ key, ...li }, option, { selected }) => (
          <li key={key} {...li}>
            <Checkbox size="small" checked={selected} sx={{ mr: 1, p: 0.5 }} />
            <OpcaoComMotivo label={getLabel(option)} motivo={getDisabledReason?.(option)} />
          </li>
        )}
      />
    );
  }

  return (
    <Autocomplete
      {...comuns}
      value={options.find((o) => o.id === props.value) ?? null}
      onChange={(_, novo) => props.onChange(novo?.id ?? null)}
      renderOption={
        getDisabledReason
          ? ({ key, ...li }, option) => (
              <li key={key} {...li}>
                <OpcaoComMotivo label={getLabel(option)} motivo={getDisabledReason(option)} />
              </li>
            )
          : undefined
      }
    />
  );
}

function OpcaoComMotivo({ label, motivo }: { label: string; motivo?: string }) {
  return (
    <Box>
      <Typography variant="body2">{label}</Typography>
      {motivo && (
        <Typography variant="caption" color="text.secondary">
          {motivo}
        </Typography>
      )}
    </Box>
  );
}
